import DashBoard from '@components/dashboard/DashBoard';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  LinearProgress,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Chip,
  useMediaQuery,
} from '@mui/material';

import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import GroupsIcon from '@mui/icons-material/Groups';

import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import MilitaryTechOutlinedIcon from '@mui/icons-material/MilitaryTechOutlined';

import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';

import {
  useSummaryThisWeek,
  useSummaryThisYear,
  useSummaryToday,
  useSummayThisMonth,
} from '@queries/summaries/useDashboardSummary';
import { useSummaryInventoryStatus } from '@queries/summaries/useSummaryInventoryStatus';

import {
  useSummarySalesByPartner,
  type DashboardKpiPreset,
} from '@queries/summaries/useSummaySalesByPartner';
import { useSummarySalesByProduct } from '@queries/summaries/useSummaySalesByProduct';

import type { GetSalesInventoryRemainedResponse } from '@apis/summary';

import { usePageHeader } from '@components/common/pageHeader.context';

import { useEffect, useMemo, useRef, useState } from 'react';
import { utcToZonedTime } from 'date-fns-tz';
import {
  INVENTORY_COVERAGE_CRITICAL_DAYS,
  INVENTORY_COVERAGE_WARNING_DAYS,
  INVENTORY_COVERAGE_SHIPPED_WINDOW_DAYS,
  INVENTORY_COVERAGE_WINDOW_LABEL,
  getInventoryCoverageLevel,
  type InventoryCoverageLevel,
} from '@constants/inventoryCoverage';
import { KOREAN_TIME_ZONE } from '@constants/timezone';

const KPI_CARD_HEIGHT = 112;
const ROW1_HEIGHT = 420;

type KpiItem = {
  title: string;
  icon: React.ReactNode;
  value: number;
  unit: string;
  loading?: boolean;
  badge?: string;
};

type RankItem = {
  rank: number;
  title: string;
  rightPrimary: string;
  rightSecondary?: string;
  valueForBar: number;
};

type InventoryItem = {
  rank: number;
  stockId: number;
  stockCode: string;
  remained: number;
  shipped7d: number;
  avgDaily: number;
  daysCover: number;
  level: InventoryCoverageLevel;
};

const PANEL_COPY = {
  salesTrend: {
    title: 'Sales Trend',
    subtitle: 'Daily totals by selected range',
  },
  inventoryCoverage: {
    title: 'Inventory Coverage',
    subtitle: `Coverage based on shipped volume (${INVENTORY_COVERAGE_WINDOW_LABEL})`,
  },
  topProducts: {
    title: 'Top Products',
    subtitle: 'Ranked by revenue (shows revenue & quantity)',
  },
  topPartners: {
    title: 'Top Partners',
    subtitle: 'Ranked by revenue',
  },
} as const;

const Home = () => {
  const [productPreset, setProductPreset] = useState<DashboardKpiPreset>('R30');
  const [partnerPreset, setPartnerPreset] = useState<DashboardKpiPreset>('R30');

  // Used for display only (KST).
  const [refreshTime, setRefreshTime] = useState(() =>
    utcToZonedTime(new Date(), KOREAN_TIME_ZONE)
  );

  const { setPageHeaderSlots } = usePageHeader();

  // KPI summary
  const todayQ = useSummaryToday();
  const weekQ = useSummaryThisWeek();
  const monthQ = useSummayThisMonth();
  const yearQ = useSummaryThisYear();

  // Inventory / ranks
  const inventoryQ = useSummaryInventoryStatus();
  const { data: salesByProduct } = useSummarySalesByProduct(productPreset);
  const { data: salesByPartner } = useSummarySalesByPartner(partnerPreset);

  // Refresh timestamp when KPI requests finish (network round trip completed).
  const kpiFetching =
    todayQ.isFetching || weekQ.isFetching || monthQ.isFetching || yearQ.isFetching;

  const prevKpiFetchingRef = useRef<boolean>(kpiFetching);

  useEffect(() => {
    const prev = prevKpiFetchingRef.current;
    if (prev && !kpiFetching) {
      setRefreshTime(utcToZonedTime(new Date(), KOREAN_TIME_ZONE));
    }
    prevKpiFetchingRef.current = kpiFetching;
  }, [kpiFetching]);

  // AppPageHeader slots
  const headerLeading = useMemo(
    () => ({
      title: 'Last updated (KST)',
      description: refreshTime.toLocaleString(),
    }),
    [refreshTime]
  );

  useEffect(() => {
    setPageHeaderSlots({ leading: headerLeading, actions: [] });
    return () => setPageHeaderSlots({ leading: undefined, actions: undefined });
  }, [headerLeading, setPageHeaderSlots]);

  const kpis: KpiItem[] = useMemo(
    () => [
      {
        title: 'Today',
        icon: <TodayIcon fontSize="small" />,
        value: todayQ.data?.data?.[0]?.amount ?? 0,
        unit: 'USD',
        loading: todayQ.isLoading,
        badge: 'KST',
      },
      {
        title: 'This week',
        icon: <DateRangeIcon fontSize="small" />,
        value: weekQ.data?.data?.[0]?.amount ?? 0,
        unit: 'USD',
        loading: weekQ.isLoading,
        badge: 'WTD',
      },
      {
        title: 'This month',
        icon: <CalendarMonthIcon fontSize="small" />,
        value: monthQ.data?.data?.[0]?.amount ?? 0,
        unit: 'USD',
        loading: monthQ.isLoading,
        badge: 'MTD',
      },
      {
        title: 'This year',
        icon: <EmojiEventsIcon fontSize="small" />,
        value: yearQ.data?.data?.[0]?.amount ?? 0,
        unit: 'USD',
        loading: yearQ.isLoading,
        badge: 'YTD',
      },
    ],
    [
      todayQ.data,
      weekQ.data,
      monthQ.data,
      yearQ.data,
      todayQ.isLoading,
      weekQ.isLoading,
      monthQ.isLoading,
      yearQ.isLoading,
    ]
  );

  const productRankItems: RankItem[] = useMemo(
    () =>
      (salesByProduct?.data || []).map((r) => ({
        rank: r.rank,
        title: r.productName,
        rightPrimary: `USD ${Number(r.amount ?? 0).toLocaleString()}`,
        rightSecondary: `Qty ${Number(r.quantity ?? 0).toLocaleString()}`,
        valueForBar: Number(r.amount ?? 0),
      })),
    [salesByProduct?.data]
  );

  const partnerRankItems: RankItem[] = useMemo(
    () =>
      (salesByPartner?.data || []).map((r) => ({
        rank: r.rank,
        title: r.partnerName,
        rightPrimary: `USD ${Number(r.amount ?? 0).toLocaleString()}`,
        valueForBar: Number(r.amount ?? 0),
      })),
    [salesByPartner?.data]
  );

  const inventoryItems: InventoryItem[] = useMemo(() => {
    const rows = (inventoryQ.data?.data ?? []) as GetSalesInventoryRemainedResponse[];

    return rows.map((row) => {
      const remained = Number(row.remained ?? 0);
      const shipped7d = Number(row.shipped ?? 0); // last 7 days (excl. today)
      const avgDaily = shipped7d / INVENTORY_COVERAGE_SHIPPED_WINDOW_DAYS;
      const daysCover = avgDaily > 0 ? remained / avgDaily : Number.POSITIVE_INFINITY;

      return {
        rank: row.rank,
        stockId: row.stockId,
        stockCode: row.stockCode,
        remained,
        shipped7d,
        avgDaily,
        daysCover,
        level: getInventoryCoverageLevel(daysCover),
      };
    });
  }, [inventoryQ.data?.data]);

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={2} sx={{ width: '100%' }}>
        {/* KPI */}
        <Grid container spacing={2} alignItems="stretch">
          {kpis.map((kpi) => (
            <Grid key={kpi.title} item xs={12} sm={6} lg={3}>
              <KpiCard {...kpi} />
            </Grid>
          ))}
        </Grid>

        {/* Row 1 */}
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} lg={8}>
            <DashboardPanel
              title={PANEL_COPY.salesTrend.title}
              subtitle={PANEL_COPY.salesTrend.subtitle}
              icon={<DateRangeIcon fontSize="small" />}
              height={ROW1_HEIGHT}
              scroll="hidden"
            >
              <Box sx={{ height: '100%', width: '100%' }}>
                <DashBoard />
              </Box>
            </DashboardPanel>
          </Grid>

          <Grid item xs={12} lg={4}>
            <DashboardPanel
              title={PANEL_COPY.inventoryCoverage.title}
              subtitle={PANEL_COPY.inventoryCoverage.subtitle}
              icon={<Inventory2Icon fontSize="small" />}
              height={ROW1_HEIGHT}
              scroll="hidden"
            >
              {inventoryQ.isLoading ? (
                <Stack spacing={1}>
                  <Skeleton variant="rounded" height={92} />
                  <Skeleton variant="rounded" height={92} />
                  <Skeleton variant="rounded" height={92} />
                </Stack>
              ) : (
                <InventoryList items={inventoryItems} />
              )}
            </DashboardPanel>
          </Grid>
        </Grid>

        {/* Row 2 */}
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} lg={6}>
            <DashboardPanel
              title={PANEL_COPY.topProducts.title}
              subtitle={PANEL_COPY.topProducts.subtitle}
              icon={<ShoppingBagIcon fontSize="small" />}
              action={<PresetToggle value={productPreset} onChange={setProductPreset} />}
              scroll="visible"
            >
              <RankingList items={productRankItems} showQuantity />
            </DashboardPanel>
          </Grid>

          <Grid item xs={12} lg={6}>
            <DashboardPanel
              title={PANEL_COPY.topPartners.title}
              subtitle={PANEL_COPY.topPartners.subtitle}
              icon={<GroupsIcon fontSize="small" />}
              action={<PresetToggle value={partnerPreset} onChange={setPartnerPreset} />}
              scroll="visible"
            >
              <RankingList items={partnerRankItems} />
            </DashboardPanel>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};

export default Home;

/* ---------------- UI blocks ---------------- */

function KpiCard({ title, icon, value, unit, loading, badge }: KpiItem) {
  return (
    <Card variant="outlined" sx={{ height: KPI_CARD_HEIGHT }}>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
          sx={{ height: '100%' }}
        >
          <Stack spacing={0.75} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'action.hover',
                }}
              >
                {icon}
              </Box>
              <Typography variant="overline" color="text.secondary" noWrap>
                {title}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="baseline">
              {loading ? (
                <Skeleton variant="text" width={130} height={36} />
              ) : (
                <Typography variant="h5" sx={{ lineHeight: 1.1, fontWeight: 800 }}>
                  {value.toLocaleString()}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {unit}
              </Typography>
            </Stack>

            <Typography variant="caption" color="text.secondary">
              Total sales
            </Typography>
          </Stack>

          {badge ? (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 999,
                border: '1px solid',
                borderColor: 'divider',
                color: 'text.secondary',
                typography: 'caption',
                flexShrink: 0,
              }}
            >
              {badge}
            </Box>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}

function DashboardPanel({
  title,
  subtitle,
  icon,
  action,
  height,
  scroll = 'auto',
  children,
}: {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  height?: number;
  scroll?: 'auto' | 'hidden' | 'visible';
  children: React.ReactNode;
}) {
  const isDownSm = useMediaQuery((t: any) => t.breakpoints.down('sm'));

  return (
    <Card
      variant="outlined"
      sx={{
        height: height ?? 'auto',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <CardHeader
        title={
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              {icon ? <Box sx={{ color: 'text.secondary' }}>{icon}</Box> : null}
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {title}
              </Typography>
            </Stack>

            {subtitle ? (
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Stack>
        }
        action={action}
        sx={{
          pb: 1,

          ...(isDownSm
            ? {
                // On narrow screens, stack header content and action vertically
                // so the action never overlaps the title/subtitle.
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 1,
                '& .MuiCardHeader-content': { minWidth: 0, width: '100%' },
                '& .MuiCardHeader-action': {
                  m: 0,
                  alignSelf: 'stretch',
                  width: '100%',
                },
              }
            : {
                '& .MuiCardHeader-action': {
                  alignSelf: 'center',
                  mt: 0,
                  ml: 1,
                  maxWidth: '100%',
                },
                '& .MuiCardHeader-content': { minWidth: 0 },
              }),
        }}
      />
      <Divider />
      <CardContent sx={{ pt: 2, flex: 1, minHeight: 0 }}>
        <Box sx={{ height: '100%', overflow: scroll, minHeight: 0 }}>{children}</Box>
      </CardContent>
    </Card>
  );
}

function PresetToggle({
  value,
  onChange,
}: {
  value: DashboardKpiPreset;
  onChange: (v: DashboardKpiPreset) => void;
}) {
  return (
    <Box
      sx={{
        width: { xs: '100%', sm: 'auto' },
        overflowX: { xs: 'auto', sm: 'visible' },
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <ToggleButtonGroup
        value={value}
        exclusive
        size="small"
        onChange={(_, v) => v && onChange(v)}
        sx={{
          flexWrap: 'nowrap',
          width: 'max-content',
          minWidth: { xs: '100%', sm: 'auto' },
          '& .MuiToggleButton-root': {
            px: 1.25,
            whiteSpace: 'nowrap',
            flex: '0 0 auto',
          },
        }}
      >
        <ToggleButton value="TODAY">Today</ToggleButton>
        <ToggleButton value="R7">Last 7 days</ToggleButton>
        <ToggleButton value="R30">Last 30 days</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
}

/* ---------------- Ranking list ---------------- */

function medalIcon(rank: number) {
  if (rank === 1) return <EmojiEventsOutlinedIcon fontSize="small" />;
  if (rank === 2) return <WorkspacePremiumOutlinedIcon fontSize="small" />;
  if (rank === 3) return <MilitaryTechOutlinedIcon fontSize="small" />;
  return null;
}

function RankingList({
  items,
  showQuantity = false,
}: {
  items: RankItem[];
  showQuantity?: boolean;
}) {
  const max = Math.max(1, ...items.map((i) => i.valueForBar));

  return (
    <Stack spacing={1}>
      {items.map((item) => {
        const pct = Math.round((item.valueForBar / max) * 100);

        return (
          <Box key={item.rank}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ width: 56, flexShrink: 0 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                  #{item.rank}
                </Typography>
                <Box sx={{ color: 'text.secondary', display: 'grid', placeItems: 'center' }}>
                  {medalIcon(item.rank)}
                </Box>
              </Stack>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 800 }}>
                  {item.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  vs Top 1: {pct}%
                </Typography>
              </Box>

              <Stack spacing={0} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                  {item.rightPrimary}
                </Typography>
                {showQuantity && item.rightSecondary ? (
                  <Typography variant="caption" color="text.secondary">
                    {item.rightSecondary}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>

            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{ mt: 0.75, height: 6, borderRadius: 999 }}
            />
          </Box>
        );
      })}
    </Stack>
  );
}

/* ---------------- Inventory list ---------------- */

function levelMeta(level: InventoryCoverageLevel) {
  switch (level) {
    case 'critical':
      return {
        chipColor: 'error' as const,
        chipVariant: 'filled' as const,
        icon: <ReportProblemOutlinedIcon fontSize="small" />,
        labelPrefix: 'Critical',
      };
    case 'warning':
      return {
        chipColor: 'warning' as const,
        chipVariant: 'filled' as const,
        icon: <WarningAmberOutlinedIcon fontSize="small" />,
        labelPrefix: 'Warning',
      };
    default:
      return {
        chipColor: 'default' as const,
        chipVariant: 'outlined' as const,
        icon: <CheckCircleOutlineIcon fontSize="small" />,
        labelPrefix: 'OK',
      };
  }
}

function InventoryList({ items }: { items: InventoryItem[] }) {
  const isDownSm = useMediaQuery((t: any) => t.breakpoints.down('sm'));

  if (!items.length) {
    return (
      <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No data
        </Typography>
      </Box>
    );
  }

  // Normalize the risk bar by the most critical item in the list.
  const maxRisk = Math.max(
    1,
    ...items.map((i) => {
      if (!Number.isFinite(i.daysCover)) return 0;
      const base = i.level === 'critical'
        ? INVENTORY_COVERAGE_CRITICAL_DAYS
        : INVENTORY_COVERAGE_WARNING_DAYS;
      return Math.max(0, base - i.daysCover);
    })
  );

  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        pr: 1,
        overscrollBehavior: 'contain',
        scrollSnapType: 'y proximity',
        scrollPaddingTop: 8,
      }}
    >
      <Stack spacing={1.25} sx={{ py: 0.5 }}>
        {items.map((it) => {
          const meta = levelMeta(it.level);

          const coverLabel = !Number.isFinite(it.daysCover)
            ? '∞'
            : `${it.daysCover.toFixed(it.daysCover < 10 ? 1 : 0)}d`;

          const base = it.level === 'critical'
            ? INVENTORY_COVERAGE_CRITICAL_DAYS
            : INVENTORY_COVERAGE_WARNING_DAYS;
          const risk = !Number.isFinite(it.daysCover) ? 0 : Math.max(0, base - it.daysCover);
          const riskPct = it.level === 'ok' ? 0 : Math.min(100, Math.round((risk / maxRisk) * 100));

          return (
            <Card
              key={it.stockId}
              variant="outlined"
              sx={{
                scrollSnapAlign: 'start',
                scrollMarginTop: 8,
                p: 1.25,
                borderRadius: 2,
                bgcolor:
                  it.level === 'critical'
                    ? 'rgba(211, 47, 47, 0.06)'
                    : it.level === 'warning'
                      ? 'rgba(255, 193, 7, 0.08)'
                      : 'transparent',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, width: '100%' }}>
                  <Box sx={{ width: 56, display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                      #{it.rank}
                    </Typography>
                    <Box
                      sx={{
                        color:
                          it.level === 'critical'
                            ? 'error.main'
                            : it.level === 'warning'
                              ? 'warning.main'
                              : 'text.secondary',
                        display: 'grid',
                        placeItems: 'center',
                      }}
                    >
                      {meta.icon}
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                    <InventoryOutlinedIcon fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                      {it.stockCode}
                    </Typography>
                  </Stack>

                  {!isDownSm ? (
                    <Chip
                      size="small"
                      color={meta.chipColor}
                      variant={meta.chipVariant}
                      label={`${meta.labelPrefix} · Cover ${coverLabel}`}
                      sx={{ ml: 'auto', flexShrink: 0 }}
                    />
                  ) : null}
                </Stack>

                {isDownSm ? (
                  <Chip
                    size="small"
                    color={meta.chipColor}
                    variant={meta.chipVariant}
                    label={`${meta.labelPrefix} · Cover ${coverLabel}`}
                    sx={{ alignSelf: 'flex-start' }}
                  />
                ) : null}
              </Stack>

              <Grid container spacing={1} sx={{ mt: 0.75 }}>
                <Grid item xs={4}>
                  <Metric label="Remained" value={it.remained.toLocaleString()} />
                </Grid>
                <Grid item xs={4}>
                  <Metric label="Shipped (7d)" value={it.shipped7d.toLocaleString()} />
                </Grid>
                <Grid item xs={4}>
                  <Metric label="Avg / day" value={it.avgDaily.toFixed(1)} />
                </Grid>
              </Grid>

              {it.level !== 'ok' ? (
                <LinearProgress
                  variant="determinate"
                  value={riskPct}
                  sx={{ mt: 1, height: 6, borderRadius: 999 }}
                  color={it.level === 'critical' ? 'error' : 'warning'}
                />
              ) : (
                <Box sx={{ mt: 1, height: 6 }} />
              )}
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: 'action.hover' }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 900 }}>
        {value}
      </Typography>
    </Box>
  );
}
